// NOTE: 定期支出機能は今は使用していない。
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

export type RecurringFrequency = "monthly" | "yearly";

export interface RecurringExpenseRecord {
  recurring_id: string;
  client_id: string;
  property_id: string;

  name: string;
  category: string;
  expense_type: "CAPEX" | "OPEX";
  amount: number;

  frequency: RecurringFrequency;
  day_of_month: number;
  start_date: string;
  end_date?: string;

  is_active: boolean;
  last_run?: string;
  next_run?: string;

  description?: string;
  budget_category_id?: string;

  created_at: string;
  updated_at?: string;
}

interface RecurringExpenseState {
  list: {
    data: RecurringExpenseRecord[];
    loading: boolean;
    error: string | null;
  };
}

const initialState: RecurringExpenseState = {
  list: { data: [], loading: false, error: null },
};

export const fetchRecurringExpenses = createAsyncThunk<
  RecurringExpenseRecord[],
  { property_id: string; is_active?: boolean }
>("recurringExpenses/fetch", async ({ property_id, is_active }, { rejectWithValue }) => {
  try {
    const params: Record<string, string> = { property_id };
    if (is_active !== undefined) params.is_active = String(is_active);
    const res = await ipponApi.get(`${BaseURL}/api/v1/recurring-expenses`, {
      params,
    });
    return res.data.data.items;
  } catch {
    return rejectWithValue("定期支出の取得に失敗しました");
  }
});

export const createRecurringExpense = createAsyncThunk<
  RecurringExpenseRecord,
  {
    property_id: string;
    name: string;
    category: string;
    expense_type?: "CAPEX" | "OPEX";
    amount: number;
    frequency: RecurringFrequency;
    day_of_month: number;
    start_date: string;
    end_date?: string;
    is_active?: boolean;
    description?: string;
    budget_category_id?: string;
  }
>("recurringExpenses/create", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/recurring-expenses`,
      body,
    );
    return res.data.data;
  } catch {
    return rejectWithValue("定期支出の登録に失敗しました");
  }
});

export const updateRecurringExpense = createAsyncThunk<
  RecurringExpenseRecord,
  { recurring_id: string; body: Partial<RecurringExpenseRecord> }
>(
  "recurringExpenses/update",
  async ({ recurring_id, body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.put(
        `${BaseURL}/api/v1/recurring-expenses/${recurring_id}`,
        body,
      );
      return res.data.data;
    } catch {
      return rejectWithValue("定期支出の更新に失敗しました");
    }
  },
);

export const deleteRecurringExpense = createAsyncThunk<
  string,
  { recurring_id: string }
>(
  "recurringExpenses/delete",
  async ({ recurring_id }, { rejectWithValue }) => {
    try {
      await ipponApi.delete(
        `${BaseURL}/api/v1/recurring-expenses/${recurring_id}`,
      );
      return recurring_id;
    } catch {
      return rejectWithValue("定期支出の削除に失敗しました");
    }
  },
);

export const runRecurringExpense = createAsyncThunk<
  { recurring_id: string; expense_id: string },
  { recurring_id: string; execution_date?: string }
>(
  "recurringExpenses/run",
  async ({ recurring_id, execution_date }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.post(
        `${BaseURL}/api/v1/recurring-expenses/${recurring_id}/run`,
        execution_date ? { execution_date } : {},
      );
      return res.data.data;
    } catch {
      return rejectWithValue("定期支出の実行に失敗しました");
    }
  },
);

const recurringExpenseSlice = createSlice({
  name: "recurringExpenses",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecurringExpenses.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchRecurringExpenses.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = action.payload;
      })
      .addCase(fetchRecurringExpenses.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload as string;
      })
      .addCase(createRecurringExpense.fulfilled, (state, action) => {
        state.list.data.unshift(action.payload);
      })
      .addCase(updateRecurringExpense.fulfilled, (state, action) => {
        const idx = state.list.data.findIndex(
          (r) => r.recurring_id === action.payload.recurring_id,
        );
        if (idx >= 0) state.list.data[idx] = action.payload;
      })
      .addCase(deleteRecurringExpense.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter(
          (r) => r.recurring_id !== action.payload,
        );
      });
  },
});

export default recurringExpenseSlice.reducer;
